import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, message, Dropdown, Button, Modal, InputNumber } from 'antd';
import type { MenuProps } from 'antd';
import { InfoCircleOutlined, BellOutlined, TeamOutlined, AppstoreOutlined, TrophyOutlined, DownOutlined, MailOutlined, EditOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { TournamentService } from '@/services/tournament.service';
import { Tournament } from '@/types/api.types';
import { logger } from '@/utils/logger';
import TournamentInfoContent from './TournamentInfoContent';
import LiveScoreboard from './LiveScoreboard';
import TournamentBracketContainer from './TournamentBracketContainer';
import TournamentParticipants from './TournamentParticipants';
import TournamentResults from './TournamentResults';
import TeamResults from './TeamResults';
import { TournamentHeader } from './TournamentHeader';
import { EditTournamentModal } from './EditTournamentModal';
import TournamentInvitationsManagement from './TournamentInvitationsManagement';
import { useAuth } from '@/contexts/AuthContext';
import {
  PageContainer,
  TabNavigation,
  StyledTabs,
  ContentWrapper
} from './styles/TournamentPage.styles';

// Компонент для вкладки с выпадающим меню
const ResultsTabLabel: React.FC<{
  activeTab: string;
  resultsSubTab: string;
  onTabChange: (key: string) => void;
  onSubMenuClick: MenuProps['onClick'];
  menuItems: MenuProps['items'];
  onDropdownClick: () => void;
}> = ({ activeTab: _activeTab, resultsSubTab, onTabChange: _onTabChange, onSubMenuClick, menuItems, onDropdownClick }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <Dropdown
      menu={{
        items: menuItems,
        onClick: (info) => {
          onSubMenuClick?.(info);
          setDropdownOpen(false);
          // Переключение вкладки теперь происходит в handleResultsSubMenuClick
        },
        selectedKeys: [resultsSubTab],
      }}
      trigger={['click']}
      placement="bottomLeft"
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      getPopupContainer={(triggerNode) => {
        // Ищем ближайший контейнер вкладок
        let parent = triggerNode.parentElement;
        while (parent && !parent.classList.contains('ant-tabs-nav')) {
          parent = parent.parentElement;
        }
        return parent || document.body;
      }}
    >
      <div
        style={{ 
          cursor: 'pointer', 
          display: 'inline-flex', 
          alignItems: 'center',
          userSelect: 'none',
          position: 'relative'
        }}
        onClick={(e) => {
          // Предотвращаем переключение вкладки при клике
          e.stopPropagation();
          e.preventDefault();
          // Уведомляем родительский компонент о клике на dropdown
          onDropdownClick();
          // При клике в любой области открываем меню
          setDropdownOpen(!dropdownOpen);
        }}
        onMouseDown={(e) => {
          // Также предотвращаем на mousedown, так как Tabs может обрабатывать это событие
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <TrophyOutlined style={{ marginRight: 8 }} />
        РЕЗУЛЬТАТЫ
        <DownOutlined style={{ fontSize: '10px', marginLeft: 4 }} />
      </div>
    </Dropdown>
  );
};

interface TournamentPageProps {}

export const TournamentPage: React.FC<TournamentPageProps> = React.memo(() => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('info');
  const [resultsSubTab, setResultsSubTab] = useState<string>('individual');
  const [isResultsDropdownClick, setIsResultsDropdownClick] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isBroadcastModalVisible, setIsBroadcastModalVisible] = useState(false);
  const [broadcastTatamis, setBroadcastTatamis] = useState<number[]>([]);
  const [broadcastSelectedTatami, setBroadcastSelectedTatami] = useState<number>(1);
  const [loadingBroadcastTatamis, setLoadingBroadcastTatamis] = useState(false);

  // Проверяем, является ли пользователь организатором турнира
  const isOrganizer = tournament && user && (
    user.role === 'admin' ||
    tournament.organizerId === user.id ||
    (user.role === 'manager' && tournament.clubId === user.clubId) ||
    (user.role === 'manager' && tournament.clubId === user.clubId)
  );

  // Проверяем, опубликованы ли сетки
  const bracketsPublished = tournament?.bracketsPublished ?? false;
  
  // Проверяем, может ли пользователь видеть участников и сетки
  const isAdmin = user?.role === 'admin';
  const isManagerOrCoach = user && (user.role === 'manager' || user.role === 'head_manager' || user.role === 'coach');
  const isTournamentClubMember = tournament && user && tournament.clubId && user.clubId && tournament.clubId === user.clubId;
  const canViewBracketsAndParticipants = isAdmin || isOrganizer || (isManagerOrCoach && isTournamentClubMember) || bracketsPublished;

  useEffect(() => {
    const fetchTournament = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const tournamentData = await TournamentService.getById(Number(id));
        setTournament(tournamentData);
        
        // Проверяем наличие матчей (для будущего использования)
        try {
          const matches = await TournamentService.getTournamentMatches(Number(id));
          const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];
          // hasMatches может использоваться в будущем для условного рендеринга
          void (matchesArray.length > 0);
        } catch (error) {
          logger.error('Error checking matches:', error);
        }
      } catch (error) {
        logger.error('Error fetching tournament:', error);
        message.error('Ошибка при загрузке турнира');
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [id]);

  // Сохранение активной вкладки в URL
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && ['info', 'updates', 'participants', 'brackets', 'results'].includes(hash)) {
      setActiveTab(hash);
    }
    // Проверяем подменю результатов
    const urlParams = new URLSearchParams(window.location.search);
    const subTab = urlParams.get('subTab');
    if (subTab && ['individual', 'team'].includes(subTab)) {
      setResultsSubTab(subTab);
    }
  }, []);

  const handleTabChange = async (key: string) => {
    // Если это клик на dropdown results, не переключаем вкладку
    if (key === 'results' && isResultsDropdownClick) {
      setIsResultsDropdownClick(false);
      return;
    }
    setIsResultsDropdownClick(false);
    
    // Если выбрана вкладка управления, перенаправляем на страницу управления
    if (key === 'management') {
      navigate(`/tournaments/${id}/management`);
      return;
    }
    
    setActiveTab(key);
    window.location.hash = key;
    if (key === 'results') {
      // При переходе на результаты, если подменю не выбрано, используем индивидуальные
      if (!resultsSubTab) {
        setResultsSubTab('individual');
      }
    }
  };

  const handleResultsDropdownClick = () => {
    setIsResultsDropdownClick(true);
  };

  const handleResultsSubMenuClick: MenuProps['onClick'] = ({ key }) => {
    setResultsSubTab(key);
    const url = new URL(window.location.href);
    url.searchParams.set('subTab', key);
    window.history.pushState({}, '', url.toString());
    // Сбрасываем флаг, чтобы разрешить переключение вкладки
    setIsResultsDropdownClick(false);
    // Всегда переходим на вкладку results после выбора пункта меню
    // Это гарантирует, что вкладка станет активной
    setActiveTab('results');
    window.location.hash = 'results';
  };

  const resultsMenuItems: MenuProps['items'] = [
    {
      key: 'individual',
      label: 'Индивидуальные результаты',
    },
    {
      key: 'team',
      label: 'Результаты команды',
    },
  ];

  if (loading || !tournament) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  const handleEditSuccess = async () => {
    setIsEditModalVisible(false);
    if (id) {
      try {
        const tournamentData = await TournamentService.getById(Number(id));
        setTournament(tournamentData);
        message.success('Турнир успешно обновлен');
      } catch (error) {
        logger.error('Error fetching tournament after update:', error);
      }
    }
  };

  const handleRegisterSuccess = async () => {
    // Перезагружаем данные турнира после регистрации участников
    if (id) {
      try {
        const tournamentData = await TournamentService.getById(Number(id));
        setTournament(tournamentData);
        message.success('Участники успешно зарегистрированы');
      } catch (error) {
        logger.error('Error fetching tournament after registration:', error);
      }
    }
  };

  const handleOpenBroadcastModal = async () => {
    if (!id) return;
    setIsBroadcastModalVisible(true);
    setLoadingBroadcastTatamis(true);
    try {
      const matches = await TournamentService.getTournamentMatches(Number(id));
      const data = Array.isArray(matches) ? matches : (matches as any)?.data ?? [];
      const tatamiSet = new Set<number>();
      data.forEach((m: { tatamiNumber?: number }) => {
        if (m.tatamiNumber) tatamiSet.add(m.tatamiNumber);
      });
      const tatamis = Array.from(tatamiSet).sort((a, b) => a - b);
      setBroadcastTatamis(tatamis);
      if (tatamis.length > 0) {
        setBroadcastSelectedTatami(tatamis[0]);
      } else {
        setBroadcastSelectedTatami(1);
      }
    } catch (error) {
      logger.error('Error loading tatamis for broadcast:', error);
      setBroadcastTatamis([1]);
      setBroadcastSelectedTatami(1);
    } finally {
      setLoadingBroadcastTatamis(false);
    }
  };

  const handleBroadcastModalOk = () => {
    if (!id) return;
    navigate(`/tournaments/${id}/broadcast/control?tatami=${broadcastSelectedTatami}`);
    setIsBroadcastModalVisible(false);
  };

  const tabItems = [
    {
      key: 'info',
      label: (
        <span>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          ИНФОРМАЦИЯ
        </span>
      ),
      children: (
        <ContentWrapper>
          <TournamentInfoContent 
            tournament={tournament} 
            activeTab={activeTab}
            onEdit={() => setIsEditModalVisible(true)}
          />
        </ContentWrapper>
      ),
    },
    ...(isOrganizer ? [{
      key: 'management',
      label: (
        <span>
          <EditOutlined style={{ marginRight: 8 }} />
          УПРАВЛЕНИЕ ТУРНИРОМ
        </span>
      ),
      children: (
        <ContentWrapper>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ fontSize: '16px', marginBottom: '20px' }}>
              Переход на страницу управления турниром...
            </p>
          </div>
        </ContentWrapper>
      ),
    }] : []),
    ...(canViewBracketsAndParticipants ? [{
      key: 'updates',
      label: (
        <span>
          <BellOutlined style={{ marginRight: 8 }} />
          ОБНОВЛЕНИЯ В РЕАЛЬНОМ ВРЕМЕНИ
        </span>
      ),
      children: (
        <ContentWrapper>
          <LiveScoreboard />
        </ContentWrapper>
      ),
    }] : []),
    ...(canViewBracketsAndParticipants ? [{
      key: 'participants',
      label: (
        <span>
          <TeamOutlined style={{ marginRight: 8 }} />
          УЧАСТНИКИ
        </span>
      ),
      children: (
        <ContentWrapper>
          <TournamentParticipants 
            tournamentId={Number(id)} 
            tournament={tournament}
            onCategoryClick={(categoryId) => {
              // Сохраняем выбранную категорию в sessionStorage
              if (id) {
                sessionStorage.setItem(`tournament_${id}_selectedCategory`, String(categoryId));
              }
              // Переключаемся на вкладку сеток
              setActiveTab('brackets');
            }}
          />
        </ContentWrapper>
      ),
    }] : []),
    ...(canViewBracketsAndParticipants ? [{
      key: 'brackets',
      label: (
        <span>
          <AppstoreOutlined style={{ marginRight: 8 }} />
          СЕТКИ
        </span>
      ),
      children: (
        <ContentWrapper>
          <TournamentBracketContainer />
        </ContentWrapper>
      ),
    }] : []),
    ...(isOrganizer && tournament?.isInviteOnly ? [{
      key: 'invitations',
      label: (
        <span>
          <MailOutlined style={{ marginRight: 8 }} />
          ПРИГЛАШЕНИЯ
        </span>
      ),
      children: (
        <ContentWrapper>
          <TournamentInvitationsManagement 
            tournamentId={Number(id)} 
            isInviteOnly={tournament.isInviteOnly}
            onInvitationsChange={handleEditSuccess}
          />
        </ContentWrapper>
      ),
    }] : []),
    {
      key: 'results',
      label: (
        <ResultsTabLabel
          activeTab={activeTab}
          resultsSubTab={resultsSubTab}
          onTabChange={handleTabChange}
          onSubMenuClick={handleResultsSubMenuClick}
          menuItems={resultsMenuItems}
          onDropdownClick={handleResultsDropdownClick}
        />
      ),
      children: (
        <ContentWrapper>
          {resultsSubTab === 'individual' ? (
            <TournamentResults tournamentId={Number(id)} tournament={tournament} />
          ) : (
            <TeamResults tournamentId={Number(id)} tournament={tournament} />
          )}
        </ContentWrapper>
      ),
    },
  ];

  return (
    <PageContainer>
      <TournamentHeader
        tournament={tournament}
        onEdit={() => setIsEditModalVisible(true)}
        onRegisterSuccess={handleRegisterSuccess}
      />
      <TabNavigation>
        <StyledTabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          size="large"
          tabBarExtraContent={
            isOrganizer && id ? (
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                onClick={handleOpenBroadcastModal}
              >
                Управление трансляцией боя
              </Button>
            ) : null
          }
        />
      </TabNavigation>
      {tournament && (
        <EditTournamentModal
          visible={isEditModalVisible}
          tournament={tournament}
          onCancel={() => setIsEditModalVisible(false)}
          onSuccess={handleEditSuccess}
        />
      )}
      <Modal
        title="Управление трансляцией боя"
        open={isBroadcastModalVisible}
        onOk={handleBroadcastModalOk}
        onCancel={() => setIsBroadcastModalVisible(false)}
        okText="Открыть"
        cancelText="Отмена"
      >
        <p style={{ marginBottom: 12 }}>Выберите татами:</p>
        {loadingBroadcastTatamis ? (
          <Spin size="small" />
        ) : (
          <InputNumber
            min={broadcastTatamis.length > 0 ? Math.min(...broadcastTatamis) : 1}
            max={broadcastTatamis.length > 0 ? Math.max(...broadcastTatamis) : 10}
            value={broadcastSelectedTatami}
            onChange={(v) => setBroadcastSelectedTatami(v ?? 1)}
            style={{ width: '100%' }}
          />
        )}
      </Modal>
    </PageContainer>
  );
});

TournamentPage.displayName = 'TournamentPage';

export default TournamentPage;

